import { useEffect, useState } from "react";
import { Download, Check, Share, Smartphone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

/**
 * MobileDownloadButton — the public-facing "Download Game" CTA.
 *
 * Unlike <PwaInstallButton> (which silently hides when the browser
 * can't auto-install), this button is ALWAYS visible and routes the
 * pilot to the right install path for their environment:
 *
 *  - Chrome / Edge / Android Chrome / Samsung Internet:
 *      `beforeinstallprompt` is captured → tap fires the NATIVE install
 *      dialog. Same UX as the navbar PwaInstallButton, but in the hero.
 *
 *  - iOS Safari (no `beforeinstallprompt` API):
 *      Tap opens an in-page modal walking through Share → "Add to
 *      Home Screen". Visual cue includes the Share icon.
 *
 *  - Desktop Safari / desktop Firefox / desktop browsers without
 *    install support: tap opens a QR-code modal pointing at the SPA
 *    with `?src=download` so the pilot can scan with their phone
 *    and install from there. This is the "works on every device"
 *    fallback the user asked for.
 *
 *  - Already-installed standalone mode: button becomes a passive
 *    "// APP INSTALLED" badge. Stays visible so the layout doesn't
 *    shift, but no longer interactive.
 *
 * Click always stamps `install-attribution-source = "direct"` (or
 * preserves an existing `qr`/`share` source — newer click wins) so
 * the admin funnel sees the touch.
 */
const KIND = {
  NATIVE: "native",       // beforeinstallprompt available
  IOS: "ios",             // iOS Safari — show share-sheet hint modal
  QR: "qr",               // desktop / unsupported — show QR modal
  INSTALLED: "installed", // already in standalone mode
};

function detectKind() {
  if (typeof window === "undefined") return KIND.QR;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator.standalone === true;
  if (standalone) return KIND.INSTALLED;
  const ua = window.navigator.userAgent || "";
  const isIos =
    /iPad|iPhone|iPod/.test(ua)
    || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
  // iOS WKWebView still has no install path — treat like iOS Safari
  // (the modal explains the manual route).
  if (isIos) return KIND.IOS;
  // Default desktop / Android pre-prompt fallback is QR. If the
  // `beforeinstallprompt` event fires later (Chrome/Android), the
  // useEffect below upgrades us to NATIVE.
  return KIND.QR;
}

export default function MobileDownloadButton({
  className = "",
  testId = "mobile-download-btn",
  variant = "primary",
}) {
  const [kind, setKind] = useState("qr");
  const [deferred, setDeferred] = useState(null);
  const [modal, setModal] = useState(null); // "ios" | "qr" | null

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    setKind(detectKind());
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setKind(KIND.NATIVE);
    };
    const onInstalled = () => {
      setDeferred(null);
      setKind(KIND.INSTALLED);
      setModal(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const stampSource = () => {
    try {
      // Only stamp `direct` if no stronger source is already set —
      // preserves the QR/share attribution chain.
      if (!window.localStorage.getItem("install-attribution-source")) {
        window.localStorage.setItem("install-attribution-source", "direct");
      }
    } catch {
      /* private mode */
    }
  };

  // Fire-and-forget click beacon. Tagged with the persisted source
  // (qr / share / tournament / direct) so the admin funnel can
  // compute click-to-install dropoff per channel. `keepalive` lets
  // the request survive the page unload that the native install
  // prompt sometimes triggers.
  const pingClick = () => {
    try {
      const source =
        window.localStorage.getItem("install-attribution-source") || "direct";
      const apiBase =
        process.env.REACT_APP_BACKEND_URL || window.location.origin;
      fetch(`${apiBase}/api/telemetry/install-click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        keepalive: true,
        body: JSON.stringify({ source }),
      }).catch(() => {});
    } catch {
      /* fail silent */
    }
  };

  const onClick = async () => {
    stampSource();
    pingClick();
    if (kind === KIND.NATIVE && deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        setDeferred(null);
        if (choice?.outcome === "accepted") {
          // `appinstalled` will fire and flip us to INSTALLED.
        }
      } catch {
        setDeferred(null);
      }
      return;
    }
    if (kind === KIND.IOS) {
      setModal("ios");
      return;
    }
    // INSTALLED or fallback QR path.
    if (kind === KIND.INSTALLED) return;
    setModal("qr");
  };

  // ----- Button styling -----
  const base =
    "inline-flex items-center justify-center gap-2 font-mono-cosmic tracking-[0.25em] uppercase transition select-none";
  const sizeCls = "px-5 py-3 text-[12px]";
  const styles =
    variant === "primary"
      ? "border border-[#ff007f] bg-[#ff007f]/15 text-[#ff007f] hover:bg-[#ff007f]/25 hover:shadow-[0_0_22px_rgba(255,0,127,0.4)]"
      : "border border-[#00ffff]/60 text-[#00ffff] hover:bg-[#00ffff]/10";

  // ----- Installed badge state -----
  if (kind === KIND.INSTALLED) {
    return (
      <div
        className={`${base} ${sizeCls} border border-[#7cffb0]/60 bg-[#7cffb0]/10 text-[#7cffb0] cursor-default ${className}`}
        data-testid={`${testId}-installed`}
      >
        <Check className="w-3.5 h-3.5" />
        App Installed
      </div>
    );
  }

  // ----- Interactive button -----
  const label =
    kind === KIND.NATIVE
      ? "Install Game"
      : kind === KIND.IOS
      ? "Add to Home Screen"
      : "Download on Mobile";

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className={`${base} ${sizeCls} ${styles} ${className}`}
        data-testid={testId}
        aria-label={label}
      >
        <Download className="w-3.5 h-3.5" />
        {label}
      </button>

      {modal === "ios" && <IosHintModal onClose={() => setModal(null)} />}
      {modal === "qr" && <QrModal onClose={() => setModal(null)} />}
    </>
  );
}

// -------------------- Modal: iOS share-sheet hint --------------------
function IosHintModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="mobile-download-ios-modal"
    >
      <div
        className="glass border border-[#ff007f]/50 max-w-md w-full p-6"
        style={{ boxShadow: "0 0 28px rgba(255,0,127,0.25)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono-cosmic text-[10px] tracking-[0.35em] uppercase text-[#ff007f] mb-1">
          // INSTALL ON iOS
        </div>
        <h2 className="font-heading font-black uppercase text-2xl mb-4 tracking-tight">
          Add to Home Screen
        </h2>
        <ol className="space-y-3 mb-4">
          <Step n="1" icon={<Share className="w-4 h-4" />}>
            Tap the <span className="text-[#ff007f]">Share</span> icon in Safari's bottom bar.
          </Step>
          <Step n="2" icon={<Smartphone className="w-4 h-4" />}>
            Scroll the share sheet and pick{" "}
            <span className="text-[#ff007f]">Add to Home Screen</span>.
          </Step>
          <Step n="3" icon={<Check className="w-4 h-4" />}>
            Confirm. The game launches like a native app — full-screen,
            offline-capable.
          </Step>
        </ol>
        <button
          type="button"
          onClick={onClose}
          className="w-full px-3 py-2 border border-white/20 hover:border-white/50 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/70 hover:text-white transition"
          data-testid="mobile-download-ios-close"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function Step({ n, icon, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex-shrink-0 w-6 h-6 border border-[#ff007f]/50 text-[#ff007f] font-mono-cosmic text-[11px] flex items-center justify-center">
        {n}
      </span>
      <span className="flex-shrink-0 w-6 h-6 text-white/65 flex items-center justify-center">
        {icon}
      </span>
      <span className="text-white/80 text-sm leading-snug">{children}</span>
    </li>
  );
}

// -------------------- Modal: QR fallback for desktop --------------------
function QrModal({ onClose }) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}/?src=download`;
  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="mobile-download-qr-modal"
    >
      <div
        className="glass border border-[#00ffff]/50 max-w-md w-full p-6 text-center"
        style={{ boxShadow: "0 0 28px rgba(0,255,255,0.25)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono-cosmic text-[10px] tracking-[0.35em] uppercase text-[#00ffff] mb-1">
          // SCAN TO INSTALL
        </div>
        <h2 className="font-heading font-black uppercase text-2xl mb-4 tracking-tight">
          Open on Your Phone
        </h2>
        <p className="text-white/70 text-sm leading-snug mb-5">
          The game installs as a one-tap home-screen app on Android &
          iOS. Point your phone camera at the code to open this site,
          then{" "}
          <span className="text-[#ffd700]">Add to Home Screen</span>.
        </p>
        <div
          className="inline-block p-3 bg-white"
          style={{ lineHeight: 0 }}
          data-testid="mobile-download-qr-svg-wrapper"
        >
          <QRCodeSVG
            value={url}
            size={180}
            bgColor="#ffffff"
            fgColor="#08080e"
            level="M"
            marginSize={0}
          />
        </div>
        <div className="mt-4 font-mono-cosmic text-[10px] tracking-[0.3em] text-white/45 break-all">
          {url}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full px-3 py-2 border border-white/20 hover:border-white/50 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/70 hover:text-white transition"
          data-testid="mobile-download-qr-close"
        >
          Close
        </button>
      </div>
    </div>
  );
}
